"use client";

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePathname } from "next/navigation";
import { useAppRouter } from "@/src/shared/hooks/useAppRouter";
import { HERO_HEIGHT } from "./HeroGallery";
import { cn } from "@/src/lib/utils";
import {
  heroFlightService,
  type IRect,
} from "@/src/shared/services/heroFlight";
import {
  findHeroFrames,
  heroFlightStore,
} from "@/src/shared/utils/heroFlightStore";
import { useIsomorphicLayoutEffect } from "@/src/shared/hooks/useIsomorphicLayoutEffect";
import ShareButton from "@/src/shared/components/ShareButton";
import { WishlistHeartButton } from "@/src/shared/features/wishlist";

/** The app's route: 300ms; the keyframes carry the photo's easing, the page fades linearly. */
const FLIGHT_MS = 300;
/** The overlay steps aside once the real page has painted under it. */
const HAND_OFF_MS = 150;
/** How long a flight waits for its page before stepping aside. */
const ARRIVAL_TIMEOUT_MS = 15_000;
/** How long the overlay waits for the details page's first photo before stepping aside. */
const PHOTO_WAIT_MS = 1_000;
/** The real sheet overlaps the photo by this much (`HeroGallery`). */
const SHEET_OVERLAP = 24;

/** A loading placeholder in the page's grey. */
const Bone: React.FC<{ className: string }> = ({ className }) => (
  <div className={cn("animate-pulse rounded-md bg-[#EBEBEB]", className)} />
);

const heroRect = (): IRect => ({
  left: 0,
  top: 0,
  width: window.innerWidth,
  height: HERO_HEIGHT,
});

/**
 * Resolves once the details page's first photo (`HeroGallery`) has loaded, failed, or kept
 * the overlay waiting too long. The card and the page ask for different widths, so it is
 * often a new download: stepping aside before it lands flashed the empty grey frame.
 */
const heroPhotoReady = (): Promise<void> => {
  const img = document.querySelector<HTMLImageElement>("img[data-hero-photo]");
  if (!img || (img.complete && img.naturalWidth > 0)) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      window.clearTimeout(timer);
      img.removeEventListener("load", done);
      img.removeEventListener("error", done);
      resolve();
    };
    const timer = window.setTimeout(done, PHOTO_WAIT_MS);
    img.addEventListener("load", done);
    img.addEventListener("error", done);
  });
};

/** Runs `then` once the page under the overlay has painted. */
const afterPaint = (then: () => void) =>
  requestAnimationFrame(() => requestAnimationFrame(then));

const fade = (el: HTMLElement, from: number, to: number, duration: number) =>
  el
    .animate([{ opacity: from }, { opacity: to }], {
      duration,
      easing: "linear",
      fill: "forwards",
    })
    .finished.catch(() => undefined);

/**
 * The app's card → details transition on a phone (Flutter's `Hero` and a 300ms fade): the
 * tapped card's photo flies along Material's arc into the details page's 300px header while
 * a white page shaped like the details page fades in over the list, then the real page takes
 * over under it. Going back flies the photo home into its card. Mounted once, in `AppShell`;
 * cards start it with `flyFromCard`, and it follows the card's link once the photo moves.
 *
 * Everything moves with the Web Animations API on `transform` and `opacity`, so the
 * compositor plays it even while the next route loads on the main thread.
 */
const HeroFlight: React.FC = () => {
  const router = useAppRouter();
  const pathname = usePathname() ?? "";
  const { flight } = heroFlightStore.useStore();

  const layer = useRef<HTMLDivElement>(null);
  const page = useRef<HTMLDivElement>(null);
  const sheet = useRef<HTMLDivElement>(null);
  const chrome = useRef<HTMLDivElement>(null);
  const frame = useRef<HTMLDivElement>(null);
  const photo = useRef<HTMLDivElement>(null);
  // The photo flies over everything, as the app's Hero does; once it lands, the sheet
  // rises over its last 24px like the real page.
  const [landed, setLanded] = useState(false);

  // The router object changes as the route does; the flight must not restart with it.
  const routerRef = useRef(router);
  routerRef.current = router;

  // The page on screen: it changes once a navigation has rendered, which is when a flight
  // hears that its page has arrived. On a back, `window.location` has already moved on
  // when `popstate` fires; this still holds the page being left.
  const shownPath = useRef(pathname);
  const onArrive = useRef<((path: string) => void) | null>(null);
  useEffect(() => {
    if (pathname === shownPath.current) return;
    shownPath.current = pathname;
    onArrive.current?.(pathname);
  }, [pathname]);

  // Going back to the card's page from its details page flies the photo home.
  useEffect(() => {
    const onPop = () => {
      const { flight: current, last } = heroFlightStore.get();
      if (current || !last) return;
      if (shownPath.current !== last.detailsPath) return;
      if (window.location.pathname !== last.fromPath) return;
      heroFlightStore.set({ flight: { ...last, direction: "close" } });
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Before the first paint, so the overlay never shows a frame out of place.
  useIsomorphicLayoutEffect(() => {
    const layerEl = layer.current;
    const pageEl = page.current;
    const sheetEl = sheet.current;
    const chromeEl = chrome.current;
    const frameEl = frame.current;
    const photoEl = photo.current;
    if (
      !flight ||
      !layerEl ||
      !pageEl ||
      !sheetEl ||
      !chromeEl ||
      !frameEl ||
      !photoEl
    )
      return;

    let cancelled = false;
    const finish = (last: typeof flight | null) => {
      if (!cancelled) heroFlightStore.set({ flight: null, last });
    };
    const fadeOut = (last: typeof flight | null) =>
      void fade(layerEl, 1, 0, HAND_OFF_MS).then(() => finish(last));
    const fly = (card: IRect, direction: "open" | "close") => {
      const keyframes = heroFlightService.flightKeyframes(
        card,
        heroRect(),
        direction,
      );
      const timing = {
        duration: FLIGHT_MS,
        easing: "linear",
        fill: "forwards",
      } as const;
      frameEl.animate(
        keyframes.map((k) => ({ transform: k.frame })),
        timing,
      );
      return photoEl
        .animate(
          keyframes.map((k) => ({ transform: k.photo })),
          timing,
        )
        .finished.catch(() => undefined);
    };
    // A navigation that never renders (offline, an error page) mustn't leave the overlay up.
    const giveUp = window.setTimeout(() => finish(null), ARRIVAL_TIMEOUT_MS);

    if (flight.direction === "open") {
      setLanded(false);
      void fade(pageEl, 0, 1, FLIGHT_MS);
      void fade(sheetEl, 0, 1, FLIGHT_MS);
      void fade(chromeEl, 0, 1, FLIGHT_MS);

      let flown = false;
      let arrived = false;
      const handOff = () => {
        if (!flown || !arrived || cancelled) return;
        void heroPhotoReady().then(() => {
          if (!cancelled) afterPaint(() => fadeOut(flight));
        });
      };
      void fly(flight.cardRect, "open").then(() => {
        if (cancelled) return;
        flown = true;
        setLanded(true);
        handOff();
      });
      onArrive.current = (path) => {
        window.clearTimeout(giveUp);
        if (path !== flight.detailsPath) return finish(null);
        arrived = true;
        handOff();
      };
      // The card's link, followed once the photo is on its way.
      const start = requestAnimationFrame(() =>
        routerRef.current.push(flight.href),
      );
      return () => {
        cancelled = true;
        cancelAnimationFrame(start);
        window.clearTimeout(giveUp);
        onArrive.current = null;
      };
    }

    // Closing: hold the header's photo over the page until the list is back, then fly
    // into the card, or just fade when the card is not on screen.
    setLanded(true);
    onArrive.current = () => {
      window.clearTimeout(giveUp);
      afterPaint(() => {
        if (cancelled) return;
        const viewport = {
          width: window.innerWidth,
          height: window.innerHeight,
        };
        const rect = findHeroFrames(flight.listingId)
          .map((el) => el.getBoundingClientRect())
          .find((r) => heroFlightService.isOnScreen(r, viewport));
        if (!rect) return fadeOut(null);
        setLanded(false);
        void fade(pageEl, 1, 0, FLIGHT_MS);
        void fade(sheetEl, 1, 0, FLIGHT_MS);
        void fade(chromeEl, 1, 0, FLIGHT_MS);
        void fly(rect, "close").then(() => finish(null));
      });
    };
    return () => {
      cancelled = true;
      window.clearTimeout(giveUp);
      onArrive.current = null;
    };
  }, [flight]);

  if (!flight) return null;

  const opening = flight.direction === "open";

  return createPortal(
    <div ref={layer} aria-hidden className="fixed inset-0 z-[1000001]">
      {/* The details page fading in, shaped like its phone layout while it loads. */}
      <div
        ref={page}
        className="absolute inset-0 bg-white"
        style={{ opacity: opening ? 0 : 1 }}
      >
        {/* The reserve bar. */}
        <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-3 border-t border-[#EBEBEB] px-6 pb-4 pt-3.5">
          <div className="flex flex-col gap-2">
            <Bone className="h-4 w-32" />
            <Bone className="h-3 w-24" />
          </div>
          <Bone className="h-[50px] w-36 rounded-full" />
        </div>
      </div>
      {/* The flying photo: a header-sized frame the keyframes move and stretch onto each
          arc box, and the photo inside scaled back so it keeps its shape. */}
      <div
        ref={frame}
        className="absolute left-0 top-0 w-screen origin-top-left overflow-hidden will-change-transform"
        style={{ height: HERO_HEIGHT, zIndex: 1 }}
      >
        <div
          ref={photo}
          className="absolute inset-0 origin-top-left bg-[#F2F2F2] bg-cover bg-center will-change-transform"
          style={{ backgroundImage: `url("${flight.src}")` }}
        />
      </div>
      {/* The real page's round back, Share and save buttons over the photo, where
          `HeroGallery` puts them, so they don't pop in when the page takes over. They only
          show: the overlay takes no taps. */}
      <div
        ref={chrome}
        className="pointer-events-none absolute inset-x-0 top-0 flex h-14 items-center gap-3 px-4 pt-[env(safe-area-inset-top)]"
        style={{ opacity: opening ? 0 : 1, zIndex: 3 }}
      >
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-sm">
          <img
            src="/icons/listing/listing_hero_back.svg"
            alt=""
            className="h-[18px] w-[18px]"
          />
        </span>
        <span className="flex-1" />
        <ShareButton variant="hero" />
        <WishlistHeartButton
          listingId={flight.listingId}
          variant="plain"
          size={18}
          className="h-9 w-9 rounded-full bg-white shadow-sm"
        />
      </div>
      {/* The white sheet over the photo: the title the card already knows, the rest loading. */}
      <div
        ref={sheet}
        className="absolute inset-x-0 rounded-t-3xl bg-white"
        style={{
          top: HERO_HEIGHT - SHEET_OVERLAP,
          opacity: opening ? 0 : 1,
          zIndex: landed ? 2 : 0,
        }}
      >
        <div className="px-5 pb-6 pt-6 text-center">
          <p className="text-2xl font-semibold leading-7 tracking-[0.2px] text-ink">
            {flight.title}
          </p>
          <Bone className="mx-auto mt-3 h-4 w-28" />
          <Bone className="mx-auto mt-2 h-4 w-60" />
        </div>
        <div className="px-4">
          <div className="flex flex-col gap-6 border-t border-[#EBEBEB] py-6">
            {[0, 1].map((row) => (
              <div key={row} className="flex items-start gap-4">
                <Bone className="mt-1 h-3 w-3 rounded-full" />
                <div className="flex flex-col gap-2">
                  <Bone className="h-3 w-16" />
                  <Bone className="h-4 w-32" />
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 border-t border-[#EBEBEB] py-6">
            <Bone className="h-14 w-14 rounded-full" />
            <div className="flex flex-col gap-2">
              <Bone className="h-4 w-40" />
              <Bone className="h-3 w-48" />
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default HeroFlight;
